import {
    ConfFormOptionsFactory,
    SpecificPropsType,
} from '@/components/form/types';
import Input from '@/components/input/pure-text.vue';
import { emitBlur, emitChange } from '@/components/form/utils/emit';
import styles from './style.module.styl';
import { defineComponent } from '@vue/composition-api';
import { Field, ZFormArrayNode, ZFormNode, ZRuleItem } from '@ks/z-form';
import { checkAppId, checkUId, MPAApplication } from '@api/plc';
import WhiteList from './white-list.vue';
import { Tooltip } from '@ks/ks-element-ui';

export const AddKID = defineComponent({
    props: (['node', 'appId', 'mpAppId'] as unknown) as SpecificPropsType<{
        node: ZFormNode<{
            id: string;
        }>;
        appId: string;
        mpAppId: string;
    }>,
    setup: (props, ctx) => {
        const rules: ZRuleItem[] = [
            {
                async asyncValidator(_, v) {
                    const res = await checkUId(
                        props.appId,
                        props.mpAppId,
                        v.id,
                    );
                    const { result, error_msg } = res.data;
                    if (result === 1) {
                        return;
                    }
                    throw Error(error_msg || '校验失败');
                },
                trigger: 'blur',
            },
        ];
        return () => (
            <Field
                node={props.node}
                preserve={false}
                trigger={[]}
                class={[styles.inline, styles.affix]}
                rules={rules}>
                {({ value, emit }: any) => (
                    <span>
                        <Input
                            value={value.id}
                            width="110"
                            onUpdate={(e: string) => {
                                emit('change', { id: e });
                            }}
                            onBlur={(e: any) => {
                                emit('blur', { id: e.target.value });
                                props.node.parent?.validateSelf();
                            }}
                            status={props.node.errorStatus}
                            statusIcon={false}
                            scopedSlots={{
                                afterInput: () => (
                                    <span
                                        class={styles.cross}
                                        onClick={() => {
                                            props.node.destroy();
                                            props.node.parent?.validateSelf();
                                        }}
                                    />
                                ),
                            }}
                        />
                        {props.node.errorMessage && (
                            <div class={styles.error}>
                                {props.node.errorMessage}
                            </div>
                        )}
                    </span>
                )}
            </Field>
        );
    },
});

export const AddKIDFormItem = defineComponent({
    props: ([
        'node',
        'remainBinding',
        'appId',
    ] as unknown) as SpecificPropsType<{
        node: ZFormArrayNode<{
            id: string;
        }>;
        remainBinding: number;
        appId: string;
    }>,
    setup: (props, ctx) => {
        const mpAppId = props.node.root.value.mpAppId;
        return () => (
            <div>
                <div class={styles.tips}>
                    配置的用户视频下可挂载小程序，还可填写
                    <span style="color: #3E55DA"> {props.remainBinding} </span>
                    个
                    <Tooltip
                        content="可填写用户数量与小程序白名单一致。如需修改，请联系小程序管理员"
                        placement="top">
                        <i class={styles.tipsIcon} />
                    </Tooltip>
                    <WhiteList mpAppId={mpAppId}>
                        <a class={styles.link}>查看当前名单</a>
                    </WhiteList>
                </div>
                {props.node.keys.map((it, i) => {
                    const n = props.node.node(i);
                    return (
                        <AddKID
                            mpAppId={mpAppId}
                            appId={props.appId}
                            node={n}
                            key={it}
                        />
                    );
                })}
                {props.node.keys.length < props.remainBinding && (
                    <span
                        onClick={async () => {
                            const err = await props.node.validateChildren();
                            if (err.length === 0) {
                                props.node.add({
                                    id: '',
                                });
                            }
                        }}>
                        <span class={styles.add}>
                            <i class={styles.addIcon} />
                            添加快手ID
                        </span>
                    </span>
                )}
            </div>
        );
    },
});

interface FormProps {
    edit: boolean;
    appId: string;
    updateRemainBinding: (num: number) => void;
    remainBinding: number;
}

export const applyPlcFormFirst: ConfFormOptionsFactory<[FormProps]> = ({
    edit,
    appId,
    updateRemainBinding,
}) => [
    {
        field: 'mpAppId',
        label: '小程序APP ID',
        render: (props, { emit }) => {
            return !edit ? (
                <Input
                    value={props.value}
                    onUpdate={emitChange(emit)}
                    onBlur={emitBlur(emit)}
                    status={props.node.errorStatus}
                />
            ) : (
                <span>{props.value}</span>
            );
        },
        rules: !edit
            ? [
                  {
                      async asyncValidator(_, v) {
                          if (!v) throw Error('请输入小程序APP ID');
                          const res = await checkAppId(appId, v);
                          const { result, data, error_msg } = res.data;
                          if (result === 1) {
                              updateRemainBinding(data.remainBinding);
                              return;
                          }
                          throw Error(error_msg || '校验失败');
                      },
                      trigger: 'blur',
                  },
              ]
            : [],
    },
    {
        field: 'applyReason',
        label: '申请原因',
        render: (props, { emit }) => (
            <Input
                type="textarea"
                height="92"
                width="495"
                value={props.value}
                onUpdate={emitChange(emit)}
            />
        ),
        rules: [
            {
                type: 'string',
                required: true,
                message: '请输入申请原因',
            },
            {
                type: 'string',
                max: 400,
                message: '申请原因超过400字',
            },
        ],
    },
];

export type MPAApplicationForm = Omit<MPAApplication, 'applyUid'> & {
    applyUid: { id: string }[];
};

export const applyPlcFormSecond: ConfFormOptionsFactory<[FormProps]> = ({
    remainBinding,
    appId,
}) => [
    {
        field: 'applyUid',
        label: '添加用户快手ID',
        render: (props, { emit }) => (
            <AddKIDFormItem
                appId={appId}
                remainBinding={remainBinding}
                node={(props.node as unknown) as ZFormArrayNode}
            />
        ),
        rules: [
            {
                type: 'array',
                max: remainBinding,
                message: '超过剩余可添加数量',
            },
            {
                validator(_, value) {
                    if (Array.isArray(value)) {
                        const map = new Set<string>();
                        for (const { id } of value) {
                            if (map.has(id)) {
                                return Error('重复添加相同的ID');
                            }
                            map.add(id);
                        }
                    }
                    return true;
                },
            },
        ],
    },
];

export const applyFormOptions = [applyPlcFormFirst, applyPlcFormSecond];
